import { Component, ElementRef, Inject, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { AnimationEvent } from '@angular/animations';

import { BehaviorSubject, combineLatest, mergeMap, Subscription, switchMap } from 'rxjs';

import { Title } from '@angular/platform-browser';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { SharedReportService } from '../../services/shared-report.service';
import { SharedReportSearchService } from '../../services/shared-report-search.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { SharedReportModel } from '../../models/shared-report.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AttributeModel } from '../../../Misc/models/attribute.model';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { SharedReportListItemModel } from '../../models/shared-report-list-item.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { SharedReportInviteDialogComponent } from '../SharedReportInviteDialog/shared-report-invite-dialog.component';
import { SharedReportSentDetailsEnum } from '../../enums/shared-report-sent-details.enum';
import { MailingListModel } from '../../../MailingLists/models/mailingList.model';
import { MailingListsService } from '../../../MailingLists/services/mailing-lists.service';
import { environment } from '../../../../../environments/environment';
import { SharedReportEditDialogComponent } from '../SharedReportEditDialog/shared-report-edit-dialog.component';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import {
    dateQueryInputAllOption,
    dateQueryInput24HoursOption,
    dateQueryInput3DaysOption,
    dateQueryInput7DaysOption,
    dateQueryInput30DaysOption,
    getDateQueryInputCustomOption
} from '../../../Forms/models/date-query-input-options.model';
import { FilterChipsComponent } from '../../../Filter/components/filterChips/filter-chips.component';
import { BlockModel } from '../../../PageBuilder/models/block.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { GenerateReportDialogComponent } from '../GenerateReportDialog/generate-report-dialog.component';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { toolbarAnimation } from '../../../Misc/animations/toolbar-animation';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { ToolbarStateService } from '@Common/TargetingShared/services/toolbar-state.service';

@Component({
    selector: 'agpr-shared-reports',
    templateUrl: './shared-reports.component.html',
    styleUrls: ['./shared-reports.component.scss'],
    providers: [
        ToolbarStateService,
        {
            provide: 'PaginatorServiceInterface',
            useExisting: SharedReportSearchService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: SharedReportSearchService
        },
        {
            provide: FilterService,
            useExisting: SharedReportSearchService
        }
    ],
    animations: [toolbarAnimation],
    standalone: false
})
export class SharedReportsComponent implements OnInit, OnDestroy {

    @ViewChild('scrollArea') scrollArea: ScrollPanelComponent;
    @ViewChild('filterChipsComponent') public filterChipsComponent: FilterChipsComponent;
    blocks: BlockModel[] = [];
    public overlayContainer: Element;

    id: string = 'sharedReports';
    reportCount: number;
    expirationCount: number = 0;

    public searchLoading = true;
    public deleteLoading = false;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    public sharedReportList: SharedReportListItemModel[];
    public focusedSharedReport: SharedReportModel;
    public selectedItems: SharedReportListItemModel[] = [];

    filtersLocked: boolean = false;

    dateFilterOptions: DropdownOptionModel[] = [
        dateQueryInputAllOption,
        dateQueryInput24HoursOption,
        dateQueryInput3DaysOption,
        dateQueryInput7DaysOption,
        dateQueryInput30DaysOption,
        getDateQueryInputCustomOption()
    ];
    startDateRange: DateQueryModel = DateQueryModel.getAllQuery();
    searchRange: SearchRangeModel = new SearchRangeModel();

    sharedReportDetails: AttributeModel[] = [];
    editSharedReportDetails: AttributeModel[] = [];
    sentDetails: AttributeModel[] = [];

    private mailingLists: MailingListModel[];
    private subscriptions: Subscription[] = [];

    actions = [
        {
            label: 'Reports.sharedReports.rhp.shareReport',
            command: () => this.shareReport()
        },
        {
            label: 'Reports.sharedReports.rhp.viewReport',
            command: () => this.viewReport()
        },
        {
            label: 'Reports.sharedReports.rhp.editReport',
            command: () => this.editReport()
        },
        {
            label: 'Reports.sharedReports.rhp.exportPdf',
            command: () => this.exportPdf()
        }
    ];


    constructor (
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        protected injector: Injector,
        public sharedReportSearchService: SharedReportSearchService,
        private sharedReportService: SharedReportService,
        private coreDialogService: CoreDialogService,
        private mailingListsService: MailingListsService,
        private ref: ElementRef,
        public titleService: Title,
        public localizationService: LocalizationService,
        private userPrefsService: UserPreferencesService,
        public toolbarStateService: ToolbarStateService,
    ) {}

    /**
     * Initialization actions
     */
    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.createSubscriptions();
        this.sharedReportSearchService.loadListAndSearch();
        this.titleService.setTitle(
            this.localizationService.transform('Reports.sharedReports.browserTitle')
        );
    }

    /**
     * Creates the subscriptions this component relies on
    */
    private createSubscriptions(): void {
        this.subscriptions.push(
            this.sharedReportSearchService.getDisplayResults().subscribe(items => {
                this.sharedReportList = items;
            }),
            this.sharedReportSearchService.getItemCount().subscribe(count => {
                this.reportCount = count;
            }),
            this.sharedReportSearchService.getSearchLoading().subscribe(loading => {
                if (this.searchLoading !== loading && !loading) {
                    this.selectedItems = [];
                }
                this.searchLoading = loading;
            }),
            this.sharedReportSearchService.getFiltersLocked().subscribe(locked => {
                this.filtersLocked = locked;
            }),
            this.sharedReportSearchService.getDateQuery().subscribe(dateQuery => {
                this.startDateRange = dateQuery;
            }),
            this.sharedReportSearchService.getExpirationCount().subscribe(count => {
                this.expirationCount = count;
            }),
            this.mailingListsService.getMailingLists().subscribe( mailingLists => {
                this.mailingLists = mailingLists;
            }),
            this.sharedReportSearchService.getFocusedSharedReport().pipe(
                switchMap(sharedReport => {
                    this.focusedSharedReport = sharedReport;
                    return combineLatest([
                        this.sharedReportSearchService.getSharedReportDetails(sharedReport),
                        this.sharedReportSearchService.getEditSharedReportDetails(sharedReport),
                    ]);
                })
            ).subscribe(([details, editDetails]) => {
                this.sharedReportDetails = details;
                this.editSharedReportDetails = editDetails;
                if (this.mailingLists && this.focusedSharedReport) {
                    this.setSentDetails();
                }
            }),
            this.sharedReportService.getDeleteLoading().subscribe(deleteLoading => {
                if (this.deleteLoading !== deleteLoading && !deleteLoading) {
                    this.selectedItems = [];
                }
                this.deleteLoading = deleteLoading;
            }),
            this.userPrefsService.getUserAppPreferences().subscribe(prefs => this.toolbarStateService.setToolbarAnimationDisabled(!prefs.accessibilityPrefs.collapseToolbars))
        );
    }

    dateRangeChanged(dateRange: DateQueryModel): void {
        this.sharedReportSearchService.setDateQuery(dateRange);
        this.sharedReportSearchService.setPageNumber(1);
        this.sharedReportSearchService.loadListAndSearch();
    }

    clearFilters() {
        this.filterChipsComponent.chipContainer.chipPopover.hide();
        this.sharedReportSearchService.setFiltersLocked(false);
        this.sharedReportSearchService.clearFilters();
        this.sharedReportSearchService.applyFilters();
    }

    toggleSelectAll(): void{
        if (this.sharedReportList.length === this.selectedItems.length) {
            this.selectNone();
        } else {
            this.selectAll();
        }
    }

    /**
     * Selects all shared reports on the page
     *
     * @returns void
     */
    public selectAll(): void {
        this.selectedItems = this.sharedReportList.slice();
    }

    /**
     * Deselects all shared reports on the page
     *
     * @returns void
     */
    public selectNone(): void {
        this.selectedItems = [];
    }

    /**
     * Removes current subscriptions on destroy
     */
    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
        this.sharedReportSearchService.registerPagingValidator(null);
        this.sharedReportSearchService.resetCachedReport();
    }

    refreshSharedReports(): void {
        this.sharedReportSearchService.loadListAndSearch();
        this.selectedItems = [];
    }

    deleteSharedReports(deleteItems: SharedReportModel[]): void {
        this.sharedReportService.deleteSharedReports(deleteItems);
    }

    shareReport(): void {
        this.coreDialogService.open(SharedReportInviteDialogComponent, {
            inputs: {
                sharedReport: this.focusedSharedReport
            }
        });
    }

    viewReport(): void {
        window.open(`${environment.sharedReportsUrl}/Report/${this.focusedSharedReport.guid}`);
    }

    editReport(): void {
        const chartDisplayElement = this.focusedSharedReport.charts[0]?.displayElement as ChartDisplayElementModel;

        if (chartDisplayElement && !chartDisplayElement.originalReportBlockId) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'Reports.sharedReportEditDialog',
                    showCancel: false,
                    okLabel: 'Reports.sharedReportEditDialog.acknowledge',
                    messageHtml: 'Reports.sharedReportEditDialog.notEditableMessage'
                },
                outputs: {
                    dialogClose: () => {}
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
        } else {
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );
            this.coreDialogService.open(SharedReportEditDialogComponent, {
                inputs: {
                    animationTrigger: animationTrigger,
                    sharedReport: this.focusedSharedReport,
                    editSharedReportDetails: this.editSharedReportDetails
                }
            });
        }
    }

    exportPdf(): void {
        this.blocks = this.focusedSharedReport.charts;
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        if (!this.focusedSharedReport.coverTitle) {
            this.focusedSharedReport.coverTitle = this.focusedSharedReport.name;
        }

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                report: null,
                isSharedReportPdfExport: true,
                charts: this.blocks,
                sharedReport: this.focusedSharedReport
            }
        };

        this.coreDialogService.open(GenerateReportDialogComponent, ioModel, this.injector);
    }

    /**
     * Sets the shared report sent details for RHP
     */
    setSentDetails(): void {
        this.sentDetails = [];

        let recipients: AttributeModel = new AttributeModel();
        if (!(this.focusedSharedReport.recipientList == null) && this.focusedSharedReport.recipientList.length !== 0) {
            recipients =  {
                id: SharedReportSentDetailsEnum.recipients,
                label: 'Reports.sharedReports.rhp.recipients',
                value: this.focusedSharedReport.recipientList,
                type: attributeTypesEnum.simpleList
            };

            this.sentDetails.push(recipients);
        }

        // @HACK only put mailing list name for now, because the recipients inside the lists might have changed, so it would mislead the users.
        // It will require a backend change to make it valid later on.
        let mailingLists: AttributeModel = new AttributeModel();
        if (this.focusedSharedReport.mailingListIds?.length > 0 ) {
            const len = this.focusedSharedReport.mailingListIds.length;
            const listNames = this.mailingLists?.filter(list => this.focusedSharedReport.mailingListIds.includes(list.id))
                .map((list) => list.name);

            mailingLists = {
                id: SharedReportSentDetailsEnum.recipients,
                label: 'Reports.sharedReports.rhp.mailingLists',
                value: listNames,
                type: attributeTypesEnum.simpleList
            };

            this.sentDetails.push(mailingLists);
        }

    }

    onAnimationStart(event: AnimationEvent) {
        this.toolbarStateService.onAnimationStart(event);
    }

    onAnimationDone(event: AnimationEvent) {
        this.toolbarStateService.onAnimationDone(event);
    }

    onScroll(scrollTop: number) {
        this.toolbarStateService.onScroll(scrollTop);
    }
}
